/**
 * Every tab's home (MASTER_PROMPT §6.2, Phase 6): mountHome(host, tab, snap).
 *
 * Above a tab's own content: a designed chart of that tab's real data
 * (homes/viz.js; the same in Lite and 3D, since drawn charts read far better
 * than 3D blocks for money, ratios and people), the facts in words for
 * screen readers (homes/specs.js), and buttons for the tab's real moves (the
 * same buttons the tab already has, so every action takes the same path).
 * Players can fold the home away; the choice is remembered per browser.
 */
import { API } from "../api.js";
import { SPECS } from "./specs.js";
import { decodeTopology } from "../world/geo.js";
import { VIZ, vzLandPaths } from "./viz.js";

const HOME_FOLD_KEY = "ges.homes.folded";
const hmEsc = (s) => String(s == null ? "" : s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

function homeFolded() {
  try { return localStorage.getItem(HOME_FOLD_KEY) === "1"; } catch (e) { return false; }
}
function setHomeFolded(v) {
  try { localStorage.setItem(HOME_FOLD_KEY, v ? "1" : "0"); } catch (e) { /* not remembered */ }
}

/** Country shapes for the footprint map (the globe's own map data), fetched once. */
let hmTopology = null;
function hmLand() {
  if (!hmTopology) hmTopology = fetch("/static/assets/geo/countries-110m.json").then((r) => r.json()).then((t) => decodeTopology(t)).catch(() => { hmTopology = null; return null; });
  return hmTopology;
}

/** Run an action: switch tab, press a button the tab already has, open a product, or scroll. */
function runHomeAction(go, body) {
  if (!go) return false;
  if (go.tab && window.ges) { window.ges.showTab(go.tab); return true; }
  if (go.product != null && window.pxOpenProduct) { window.pxOpenProduct(go.product); return true; }
  if (go.clickText) {
    const want = go.clickText.toLowerCase();
    const btn = [...body.querySelectorAll("button")].find((b) => !b.disabled && b.textContent.trim().toLowerCase().includes(want));
    if (btn) { btn.scrollIntoView && btn.scrollIntoView({ block: "center", behavior: "smooth" }); btn.click(); return true; }
    return false;
  }
  if (go.selector) {
    const el = body.querySelector(go.selector);
    if (el) { el.scrollIntoView && el.scrollIntoView({ block: "center", behavior: "smooth" }); el.click(); return true; }
    return false;
  }
  if (go.scroll) {
    const el = body.querySelector(go.scroll);
    if (el && el.scrollIntoView) { el.scrollIntoView({ block: "start", behavior: "smooth" }); return true; }
  }
  return false;
}

/** The action a chart element asks for (see homes/viz.js). */
function vizGo(el) {
  const ds = el.dataset;
  if (ds.vzTab) return { tab: ds.vzTab };
  if (ds.vzSel) return { selector: ds.vzSel };
  if (ds.vzClick) return { clickText: ds.vzClick };
  return { scroll: ds.vzScroll };
}

function homeHtml(spec, model, folded, viz) {
  const facts = model.facts.map((f) => `<li class="${f.tone ? `hm-${f.tone}` : ""}"><span>${hmEsc(f.label)}</span><b>${hmEsc(f.value)}</b></li>`).join("");
  // with a chart, its own items are tappable: keep only the tab-wide moves as buttons
  const actions = model.actions.map((a, i) => (viz != null && /:/.test(a.ref || "") ? "" : `<button type="button" data-hm-act="${i}">${hmEsc(a.label)}</button>`)).join("");
  return `
    <div class="hm-head">
      <span class="hm-kicker">${hmEsc(spec.title)}</span>
      <button type="button" class="hm-fold" data-hm-fold aria-expanded="${!folded}">${folded ? "Show" : "Hide"}</button>
    </div>
    <div class="hm-body${folded ? " hidden" : ""}">
      ${viz != null ? `<div class="hm-viz">${viz}</div>` : `<div class="hm-stage"></div>`}
      <ul class="hm-facts${viz != null ? " sr-only" : ""}">${facts}</ul>
      ${actions ? `<div class="hm-actions">${actions}</div>` : ""}
      <p class="hm-scale">${hmEsc(model.scale || "")}</p>
    </div>`;
}

/**
 * Mount the home for `tab` into `host` (the space above the tab's content).
 * `body` is the tab's own content element (for pressing its buttons).
 * Returns { dispose() } or null when the tab has no home.
 */
function mountHome(host, tab, snap, body) {
  const spec = SPECS[tab];
  if (!spec || !host) return null;
  let disposed = false;
  host.classList.add("hm");
  host.innerHTML = "";
  (async () => {
    const data = {};
    await Promise.all(spec.endpoints.map(async (ep) => { data[ep] = await fetch(`${API}${ep}`).then((r) => r.json()); }));
    if (disposed || !host.isConnected) return;
    let model;
    try { model = spec.build(data, snap || {}); } catch (e) { console.warn(`home for ${tab}:`, e); host.remove(); return; }
    let viz = null;
    if (VIZ[tab]) {
      try { viz = VIZ[tab](data, snap || {}); } catch (e) { console.warn(`chart for ${tab}:`, e); }
    }
    const folded = homeFolded();
    host.innerHTML = homeHtml(spec, model, folded, viz);
    host.classList.toggle("hm-has-viz", viz != null);
    const land = host.querySelector("[data-vz-land]");
    if (land) hmLand().then((countries) => { if (countries && land.isConnected) land.innerHTML = vzLandPaths(countries, JSON.parse(land.dataset.vzLand || "{}")); });
    const stageEl = host.querySelector(".hm-stage");
    host.addEventListener("click", (e) => {
      if (e.target.closest("[data-hm-fold]")) {
        const now = !host.querySelector(".hm-body").classList.toggle("hidden");
        setHomeFolded(!now);
        e.target.textContent = now ? "Hide" : "Show";
        e.target.setAttribute("aria-expanded", String(now));
        return;
      }
      const a = e.target.closest("[data-hm-act]");
      if (a) { runHomeAction(model.actions[+a.dataset.hmAct].go, body || document); return; }
      const v = e.target.closest("[data-vz-tab], [data-vz-click], [data-vz-sel], [data-vz-scroll]");
      if (v) runHomeAction(vizGo(v), body || document);
    });
    host.addEventListener("keydown", (e) => {
      const v = (e.key === "Enter" || e.key === " ") && e.target.closest && e.target.closest("[data-vz-tab], [data-vz-click], [data-vz-sel], [data-vz-scroll]");
      if (v) { e.preventDefault(); runHomeAction(vizGo(v), body || document); }
    });
    // no designed chart (it failed to draw): the facts strip carries the story
    if (stageEl) stageEl.remove();
  })().catch((e) => { console.warn(`home for ${tab}:`, e); if (host.isConnected) host.remove(); });
  return { dispose() { disposed = true; } };
}

export { mountHome, runHomeAction };
